// Vehicles on a track (WS-13): a generic, config-driven feature. Off unless resort.json has `tracks`.
//   resort.tracks[] = { id, place?, centerline: [[x, z], ...], closed, width, surface: 'ice' | 'snow',
//     banks?: { height, width }, lamps?: [[x, z], ...], lampHeight?,
//     direction?: 'ccw' | 'cw' (driving sense seen from above), vehicles?: { kind: 'kart', count, topSpeed,
//     cornerAccel, accel, brake, slide, minGap, startGap, margin, lodDistance, headlights, spray },
//     camera?: { label, distance, height }, src, confirm }
// The centreline is a closed (or open) polyline in local metres, smoothed with a Catmull-Rom spline. The
// surface, snow banks and lamps are built on heightAt(); vehicles follow a speed profile from the curvature
// (they brake before corners and slide a little on ice), with headlights and tyre spray. The "watch" camera that
// chases the leading vehicle is the shared ride view (WS-85: lazy/rideview.js, its kart mount, from `views()`).
// trackFromPath (WS-85) = a whole track entry on a drawn loop (Fine-tune). Schema + method: skill references/landscape.md ("tracks").
import * as THREE from 'three/webgpu';
import { float, vec2, vec3, vec4, uniform, uv, texture, instanceIndex, instancedBufferAttribute, positionGeometry, positionView, screenDPR, mod,
  smoothstep, clamp, pow, length } from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { lengthOf } from './lines/measure.js'; // VIS-219: a drawn loop's length, the shared sum
import { kartGeometry, wheelGeometry, numberAtlas, KART_WHEELS, KART_WHEELBASE } from './kart.js'; // WS-13 round 4b: photo-based e-kart
import { capture } from './capture.js'; // a capture (Film, the Renders tab): the full karts at any distance
import { lifePace } from './lifepace.js'; // VIS-267 step 3: a kart is posed only where it can be seen

const merge = (list) => mergeGeometries(list.map((g) => (g.index ? g.toNonIndexed() : g)));

function canvasTex(w, h, draw, repeat = true) {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  draw(c.getContext('2d'), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}
// Ice: pale blue-grey with polished lanes and fine tyre scratches along the driving direction (u = along).
const iceTexture = () => canvasTex(512, 256, (g, w, h) => {
  g.fillStyle = '#b4c8da'; g.fillRect(0, 0, w, h);
  let s = 7; const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  for (let i = 0; i < 70; i++) { // snow dust drifted onto the ice
    const x = rnd() * w, y = rnd() * h, r = 10 + rnd() * 50, grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, 'rgba(250,252,255,0.22)'); grd.addColorStop(1, 'rgba(250,252,255,0)');
    g.fillStyle = grd; g.fillRect(x - r, y - r, 2 * r, 2 * r);
  }
  for (let i = 0; i < 260; i++) { // tyre scratches
    const y = h * (0.12 + rnd() * 0.76), x = rnd() * w, len = 40 + rnd() * 220;
    g.strokeStyle = rnd() > 0.5 ? 'rgba(120,145,170,0.22)' : 'rgba(255,255,255,0.35)';
    g.lineWidth = 0.6 + rnd() * 1.6;
    g.beginPath(); g.moveTo(x, y); g.bezierCurveTo(x + len / 3, y + (rnd() - 0.5) * 8, x + (2 * len) / 3, y + (rnd() - 0.5) * 8, x + len, y + (rnd() - 0.5) * 10); g.stroke();
  }
  const edge = g.createLinearGradient(0, 0, 0, h); // packed snow at the edges
  edge.addColorStop(0, 'rgba(244,247,251,0.95)'); edge.addColorStop(0.1, 'rgba(244,247,251,0)');
  edge.addColorStop(0.9, 'rgba(244,247,251,0)'); edge.addColorStop(1, 'rgba(244,247,251,0.95)');
  g.fillStyle = edge; g.fillRect(0, 0, w, h);
});
const glowTexture = () => canvasTex(64, 64, (g, w) => {
  const grd = g.createRadialGradient(w / 2, w / 2, 0, w / 2, w / 2, w / 2);
  grd.addColorStop(0, 'rgba(255,255,255,1)'); grd.addColorStop(0.25, 'rgba(255,255,255,0.45)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, w, w);
}, false);

/** Sample a smoothed centreline every ~1 m: positions, heading, signed curvature, arc length. */
function sampleTrack(cfg, heightAt) {
  let line = cfg.centerline;
  if (cfg.direction === 'cw' || cfg.direction === 'ccw') { // driving sense seen from above (north up): reverse the point order if needed
    let ar = 0; for (let i = 0; i < line.length; i++) { const [x0, z0] = line[i], [x1, z1] = line[(i + 1) % line.length]; ar += x0 * -z1 - x1 * -z0; }
    if ((ar > 0) !== (cfg.direction === 'ccw')) line = line.slice().reverse();
  }
  const pts = line.map(([x, z]) => new THREE.Vector3(x, 0, z));
  const closed = cfg.closed !== false;
  const curve = new THREE.CatmullRomCurve3(pts, closed, 'centripetal', 0.5);
  const len = curve.getLength(), n = Math.max(16, Math.round(len));
  const P = curve.getSpacedPoints(n); // n+1 points, last == first when closed
  if (closed) P.pop();
  const N = P.length, ds = len / (closed ? N : N - 1);
  const x = new Float32Array(N), z = new Float32Array(N), y = new Float32Array(N), hd = new Float32Array(N), k = new Float32Array(N);
  const idx = (i) => (closed ? (i + N) % N : Math.max(0, Math.min(N - 1, i)));
  for (let i = 0; i < N; i++) { x[i] = P[i].x; z[i] = P[i].z; }
  for (let i = 0; i < N; i++) { const a = idx(i - 1), b = idx(i + 1); hd[i] = Math.atan2(z[b] - z[a], x[b] - x[a]); }
  for (let i = 0; i < N; i++) { const a = idx(i - 2), b = idx(i + 2); let d = hd[b] - hd[a]; d = Math.atan2(Math.sin(d), Math.cos(d)); k[i] = d / (4 * ds); }
  // Surface height: terrain under the centreline, smoothed along the track (a ploughed track has no bumps).
  const raw = Array.from({ length: N }, (_, i) => heightAt(x[i], z[i]));
  for (let i = 0; i < N; i++) { let s = 0, c = 0; for (let j = -4; j <= 4; j++) { s += raw[idx(i + j)]; c++; } y[i] = s / c; }
  return { N, ds, len, closed, x, z, y, hd, k, idx };
}

/** Ribbon geometry across the track: offsets [o0..o1] from the centreline, heights from fn(i, offset). */
function ribbon(T, offsets, yAt, uScale = 1 / 8, vSpan = 1) {
  const cols = offsets.length, rows = T.closed ? T.N + 1 : T.N, pos = [], uv = [], index = [];
  for (let r = 0; r < rows; r++) {
    const i = r % T.N, nx = -Math.sin(T.hd[i]), nz = Math.cos(T.hd[i]); // left normal (x east, z south)
    for (let c = 0; c < cols; c++) {
      const o = offsets[c], px = T.x[i] + nx * o, pz = T.z[i] + nz * o;
      pos.push(px, yAt(i, o, px, pz), pz);
      uv.push(r * T.ds * uScale, (c / (cols - 1)) * vSpan);
    }
  }
  for (let r = 0; r < rows - 1; r++) for (let c = 0; c < cols - 1; c++) {
    const a = r * cols + c, b = a + 1, d = a + cols, e = d + 1;
    index.push(a, b, d, b, e, d); // counter-clockwise seen from above (c grows along the left normal)
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(index);
  g.computeVertexNormals();
  return g;
}

// WS-75: node materials. `material.uniforms` keeps the uniforms the frame loop sets (.value).
const ADD = { transparent: true, depthWrite: false, blending: THREE.AdditiveBlending };
const POOL_LIFT = 0.08; // m a pool of light lies over the ice, banks or snow under it (createTracks lay)
/** Headlight beam (instanced cone): fades along its length (the cone's own x, before the instance matrix) and out
 *  before the ice (its own y: 0 = the kart's wheels on the ice; the cone's lower half went into the ice, cut off along
 *  a sharp line: the pool lights the ice). */
function beamMaterial(len) {
  const U = { k: uniform(0), len: uniform(len) }, m = new THREE.MeshBasicNodeMaterial({ ...ADD, side: THREE.DoubleSide, forceSinglePass: true }); // WS-75 perf: one draw for both faces (additive: the order of the faces does not matter)
  const t = clamp(positionGeometry.x.div(U.len), 0.0, 1.0);
  m.colorNode = vec3(1.0, 0.96, 0.88).mul(U.k); m.opacityNode = U.k.mul(0.2).mul(pow(t.oneMinus(), 2.0)).mul(smoothstep(0.0, 0.35, positionGeometry.y));
  m.uniforms = U;
  return m;
}
/** Headlight pool on the ice (instanced quad): a soft ellipse. (Exported: lazy/snowmobile.js lights the snow with it.) */
export function poolMaterial() {
  const U = { k: uniform(0) }, m = new THREE.MeshBasicNodeMaterial({ ...ADD, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
  const a = smoothstep(0.0, 0.5, length(uv().sub(0.5).mul(vec2(1.0, 1.6)))).oneMinus();
  m.colorNode = vec3(1.0, 0.95, 0.85).mul(U.k); m.opacityNode = a.mul(U.k).mul(0.38);
  m.uniforms = U;
  return m;
}
/** Points bigger than 1 px are instanced sprites in WebGPU (a Points primitive is 1 px): one draw call of n sprites.
 *  VIS-267 step 3: three culls a sprite by its own position (a 0.7 m ball): `ball` = the world sphere its points stay in
 *  (kept by the caller), or a function giving it (null = nothing to draw), tested against each camera that draws it. */
function sprites(material, n, ball) {
  const p = new THREE.Sprite(material);
  p.count = n; p.raycast = () => {};
  p.intersectsFrustum = (f) => { const b = typeof ball === 'function' ? ball() : ball; return !!b && f.intersectsSphere(b); };
  return cullAfterFirst(p);
}
/** VIS-267 step 3: not culled until its first draw or compile (the warm start's compileAsync leaves out what is outside
 *  the opening view: a kart there built its programs at first sight, a stall), then culled by its true bounds */
function cullAfterFirst(o) {
  o.frustumCulled = false;
  o.onAfterRender = () => { o.frustumCulled = true; delete o.onAfterRender; };
  return o;
}
const KART_R = 1.2, KART_H = 0.7; // VIS-267 step 3: a kart's sphere (m, about its middle 0.35 m up) and height, for the life pace
const BEAM_R = 10.5; // its headlight beam and pool reach this far ahead: the sphere of a kart whose lights are on

/** WS-85: a whole resort.tracks[] entry for karts on a drawn closed loop `pts` ([[x, z], ...], the last point back at the
 *  first), with the live ice karting track's defaults (its camera block too); null when the loop cannot carry karts, the
 *  reason in plain words in trackFromPath.why. */
export function trackFromPath({ id, pts, name, seasons }) {
  const P = (pts || []).map(([x, z]) => [x, z]), d = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const len = lengthOf(P);
  trackFromPath.why = P.length < 4 || d(P[0], P.at(-1)) > 5 ? 'The line is not a closed loop.' : len < 80 ? 'The loop is too short for karts (it needs at least 80 m).' : '';
  if (trackFromPath.why) return null;
  if (d(P[0], P.at(-1)) < 0.01) P.pop();
  return { id, place: id, name: name || 'Ice karting', closed: true, centerline: P, width: 7, surface: 'ice', banks: { height: 0.6, width: 1.6 }, lampHeight: 6,
    vehicles: { kind: 'kart', count: 4, topSpeed: 10, cornerAccel: 2.6, accel: 2.2, brake: 4, slide: 0.11, minGap: 6, startGap: 16, margin: 1.3, headlights: true, spray: true },
    camera: { label: 'Watch a race', distance: 11, height: 4.2 }, seasons: seasons || ['winter'] };
}
trackFromPath.why = '';

export function createTracks({ scene, M, heightAt, tracks = [] }) {
  const list = (Array.isArray(tracks) ? tracks : []).filter((t) => Array.isArray(t.centerline) && t.centerline.length > 2);
  const state = [];
  let lightK = 0;
  const glowTex = list.length ? glowTexture() : null;
  for (const cfg of list) {
    const T = sampleTrack(cfg, heightAt), w = cfg.width ?? 7, half = w / 2, group = new THREE.Group();
    group.name = `track-${cfg.id}`;
    // Surface: a ribbon a few cm above the drawn terrain (lateral heights follow the cross slope).
    const surfY = (i, o, px, pz) => T.y[i] * 0.6 + heightAt(px, pz) * 0.4 + 0.07;
    const iceMat = cfg.surface === 'snow'
      ? M.snow
      : new THREE.MeshStandardNodeMaterial({ map: iceTexture(), color: 0xffffff, roughness: 0.12, metalness: 0.08, envMapIntensity: 1.3, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
    const offs = Array.from({ length: 7 }, (_, c) => -half + (c / 6) * w);
    const surf = new THREE.Mesh(ribbon(T, offs, surfY, 1 / 16), iceMat);
    surf.receiveShadow = true;
    group.add(surf);
    const bh = cfg.banks === false ? 0 : cfg.banks?.height ?? 0.6, bw = cfg.banks?.width ?? 1.6, bankUp = (p) => bh * Math.sin(Math.PI * Math.min(1, p * 1.15)) ** 0.8 * (1 - p * 0.15); // (p: 0 at the ice's edge, 1 at the bank's foot)
    // Where the light lies: the ice as drawn, the banks' rounded tops, else the snow, at (px, pz) from centreline sample
    // i near it (nearI walks to the nearest). A flat pool sank into the ice's cross slope, the banks and the snow beside
    // them and was cut off along a sharp line, as the snowmobiles' did (#442): the pools are laid on this instead.
    const nearI = (px, pz, i) => {
      const d = (j) => (T.x[j] - px) ** 2 + (T.z[j] - pz) ** 2;
      for (let b = d(i); ;) { const a = T.idx(i - 1), c = T.idx(i + 1), da = d(a), dc = d(c); if (da < b && da <= dc) { i = a; b = da; } else if (dc < b) { i = c; b = dc; } else return i; }
    };
    const drawnAt = (px, pz, i) => {
      const o = (pz - T.z[i]) * Math.cos(T.hd[i]) - (px - T.x[i]) * Math.sin(T.hd[i]), p = (Math.abs(o) - half) / bw, ice = surfY(i, o, px, pz);
      return Math.max(heightAt(px, pz), p <= 0 ? ice : p < 1 && bh ? ice - 0.05 + bankUp(p) : -Infinity); // (the snow where it shows through, on a cross slope)
    };
    /** Lay a pool's points `L` (flat, local: +x ahead, ~1 m apart) at (x, z) facing `h` into `A` from offset `at`,
     *  `POOL_LIFT` over the highest drawn ground within half a metre (a bank's ridge between two points cut the pool
     *  into dark notches); i: a centreline sample near (x, z). */
    const lay = (A, at, L, x, z, h, i) => {
      const c = Math.cos(h), s = Math.sin(h);
      for (let j = 0; j < L.length; j += 3) {
        const px = x + L[j] * c - L[j + 2] * s, pz = z + L[j] * s + L[j + 2] * c;
        i = nearI(px, pz, i); A[at + j] = px; A[at + j + 2] = pz;
        A[at + j + 1] = Math.max(drawnAt(px, pz, i), drawnAt(px + 0.5, pz, i), drawnAt(px - 0.5, pz, i), drawnAt(px, pz + 0.5, i), drawnAt(px, pz - 0.5, i)) + POOL_LIFT;
      }
    };
    // Snow banks pushed up by the plough on both sides.
    if (cfg.banks !== false) {
      const prof = [0, 0.2, 0.5, 0.8, 1];
      for (const side of [-1, 1]) {
        const o = prof.map((p) => side * (half + p * bw));
        const g = ribbon(T, side > 0 ? o : o.slice().reverse(), (i, oo, px, pz) => surfY(i, oo, px, pz) - 0.05 + bankUp((Math.abs(oo) - half) / bw), 1 / 6);
        const m = new THREE.Mesh(g, M.snow); m.castShadow = m.receiveShadow = true; group.add(m);
      }
    }
    // Lamp posts where the photos show them (positions in config): dark pole, arm, LED head, night glow.
    const lamps = (cfg.lamps || []).map(([x, zz]) => ({ x, z: zz, y: heightAt(x, zz) }));
    let glow = null, pools = null;
    if (lamps.length) {
      const H = cfg.lampHeight ?? 6, poles = [], heads = [];
      for (const l of lamps) {
        poles.push(new THREE.CylinderGeometry(0.07, 0.1, H, 6).translate(l.x, l.y + H / 2, l.z));
        poles.push(new THREE.BoxGeometry(0.9, 0.08, 0.08).translate(l.x + 0.4, l.y + H - 0.1, l.z));
        heads.push(new THREE.BoxGeometry(0.42, 0.1, 0.22).translate(l.x + 0.8, l.y + H - 0.18, l.z));
      }
      const pm = new THREE.Mesh(merge(poles), M.post); pm.castShadow = true;
      group.add(pm, new THREE.Mesh(merge(heads), M.lamp));
      const gp = new Float32Array(lamps.flatMap((l) => [l.x + 0.8, l.y + H - 0.3, l.z]));
      const gm = new THREE.PointsNodeMaterial({ map: glowTex, size: 7, color: 0xfff1d8, ...ADD, opacity: 0 }); // size attenuated as before
      gm.positionNode = instancedBufferAttribute(new THREE.InstancedBufferAttribute(gp, 3));
      const gb = new THREE.Sphere().setFromPoints(lamps.map((l) => new THREE.Vector3(l.x + 0.8, l.y + H - 0.3, l.z))); gb.radius += 4; // (a glow is 7 m across)
      glow = sprites(gm, lamps.length, gb);
      const lampPool = new THREE.PlaneGeometry(12, 12, 12, 12).rotateX(-Math.PI / 2), L = lampPool.attributes.position.array; // (a point a metre: laid once on the ice, banks and snow under it)
      const lampPools = lamps.map((l) => { const g = lampPool.clone(), x = l.x + 0.8; let i = 0, b = Infinity; for (let j = 0; j < T.N; j++) { const d = (T.x[j] - x) ** 2 + (T.z[j] - l.z) ** 2; if (d < b) { b = d; i = j; } } lay(g.attributes.position.array, 0, L, x, l.z, 0, i); return g; });
      pools = new THREE.Mesh(merge(lampPools),
        new THREE.MeshBasicNodeMaterial({ map: glowTex, color: 0xffeed6, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 }));
      pools.renderOrder = 2;
      group.add(glow, pools);
    }
    // ---- Vehicles (round 4b): a minimum-curvature racing line inside the track, a speed profile on it (corner
    // limit sqrt(cornerAccel / curvature), acceleration out of bends, braking distance before them), then each
    // kart integrates its speed along the line: smooth positions (no snapping), a small slide on ice, front
    // wheels that steer (from the line's curvature, counter-steer in a slide) and wheels that roll.
    // VIS-267 step 3: the race (logic) runs every frame wherever the track is; a kart is posed (its matrices, spray,
    // beam) only when lifePace says it can be seen, each mesh keeps the true bounds of its instances (culled out of
    // view, in every pass), and the body's per-kart spheres (shadowBounds) tell the shadow keeper and the rest where the
    // karts in view are (the shadow-live karts' live map then covers only those).
    const V = cfg.vehicles || null, karts = [], balls = [];
    let parts = null, beams = null, poolsK = null, spray = null, R = null;
    if (V && (V.count ?? 0) > 0) {
      R = racingLine(T, half - (V.margin ?? 1.3), V);
      const n = Math.min(8, V.count);
      const kartBalls = (cb) => { for (const b of balls) if (b.seen) cb(b.s, 1); }; // (the karts posed in the last second)
      const mk = (geo, mat, count = n, body = false) => {
        const m = cullAfterFirst(new THREE.InstancedMesh(geo, mat, count)); m.castShadow = true; group.add(m);
        m.userData.shadowLive = true; m.userData.shadowBounds = body ? kartBalls : () => {}; // (the other parts are inside the body's spheres)
        return m;
      };
      const bodyMat = new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.42, metalness: 0.35, envMapIntensity: 1.1, side: THREE.DoubleSide });
      const wheelMat = new THREE.MeshStandardNodeMaterial({ vertexColors: true, roughness: 0.75, metalness: 0.2 });
      const ledFMat = new THREE.MeshStandardNodeMaterial({ color: 0xf4f8ff, emissive: 0xeef4ff, emissiveIntensity: 1.2 });
      const ledRMat = new THREE.MeshStandardNodeMaterial({ color: 0x5a0606, emissive: 0xff2414, emissiveIntensity: 0.8 });
      const plateMat = new THREE.MeshStandardNodeMaterial({ roughness: 0.5, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1 });
      // each instance shows its own number from the digit atlas (u shifted by the instance, in the vertex stage)
      plateMat.colorNode = texture(numberAtlas(), vec2(uv().x.add(mod(float(instanceIndex), 8.0)).div(8.0), uv().y).toVarying('vPlateUv'));
      const hi = kartGeometry('hi'), lo = kartGeometry('lo');
      parts = {
        hi: [mk(hi.body, bodyMat, n, true), mk(hi.plate, plateMat), mk(hi.ledF, ledFMat), mk(hi.ledR, ledRMat), mk(wheelGeometry('hi'), wheelMat, n * 4)],
        lo: [mk(lo.body, bodyMat, n, true), mk(lo.ledF, ledFMat), mk(lo.ledR, ledRMat), mk(wheelGeometry('lo'), wheelMat, n * 4)],
        mats: { ledFMat, ledRMat }, useHi: true,
      };
      for (const m of parts.lo) { m.visible = false; m.castShadow = false; }
      if (V.headlights !== false) {
        const bl = 9;
        beams = new THREE.InstancedMesh(new THREE.ConeGeometry(1.7, bl, 14, 1, true).rotateZ(Math.PI / 2).translate(bl / 2 + 1.0, 0.25, 0),
          beamMaterial(bl + 1), n);
        const pool = new THREE.PlaneGeometry(9, 5.5, 9, 6).rotateX(-Math.PI / 2).translate(6, 0, 0); // ahead of the nose; a point a metre, laid on the ice every frame (lay)
        poolsK = new THREE.Mesh(mergeGeometries(Array.from({ length: n }, () => pool)), poolMaterial()); // (one draw for all karts, as the instanced quad was)
        poolsK.userData.local = pool.attributes.position.array;
        cullAfterFirst(beams); poolsK.frustumCulled = false; // (VIS-267 step 3: the beams culled by their instances' bounds; the pools' vertices are laid again each pose)
        for (const m of [beams, poolsK]) { m.renderOrder = 2; m.visible = false; group.add(m); }
      }
      if (V.spray !== false) {
        const SN = 320, sp = new Float32Array(SN * 3), life = new Float32Array(SN);
        const posA = new THREE.InstancedBufferAttribute(sp, 3), lifeA = new THREE.InstancedBufferAttribute(life, 1); // WS-75 perf: static usage (r186 re-uploads dynamic buffers on every render call); the loop sets needsUpdate
        // a few hundred CPU particles: kept on the CPU (cheap); sprites of 300 x (0.1 + 0.22 (1 - life)) / depth device px
        const U = { scale: uniform(300), tint: uniform(new THREE.Color(0xf4f8fc)) }, lifeN = instancedBufferAttribute(lifeA);
        const smat = new THREE.PointsNodeMaterial({ transparent: true, depthWrite: false, sizeAttenuation: false, alphaTest: 0.01 });
        smat.positionNode = instancedBufferAttribute(posA);
        smat.sizeNode = U.scale.mul(lifeN.oneMinus().mul(0.22).add(0.1)).div(positionView.z.negate()).div(screenDPR);
        smat.colorNode = vec4(U.tint, 1.0);
        smat.opacityNode = smoothstep(0.0, 0.5, length(uv().sub(0.5))).oneMinus().mul(lifeN).mul(0.55);
        smat.uniforms = U;
        const ball = new THREE.Sphere(); // VIS-267 step 3: round the puffs in the air (stepSpray); none: nothing drawn
        spray = { pts: null, pos: sp, life, posA, lifeA, vel: new Float32Array(SN * 3), next: 0, N: SN, alive: 0, ball };
        spray.pts = sprites(smat, SN, () => (spray.alive ? ball : null));
        group.add(spray.pts);
      }
      const gap = V.startGap ?? 14;
      for (let i = 0; i < n; i++) {
        const s0 = (((R.len - i * gap) % R.len) + R.len) % R.len; // a little race: karts lined up behind each other
        karts.push({ s: s0, v: R.v[R.at(s0).i] * 0.8, skill: 1 - ((i * 37) % 9) / 100, lane: ((i % 3) - 1) * 0.35, lat: 0, beta: 0, steer: 0, spin: i * 1.7, pitch: 0, braking: 0, throttle: 0, emit: 0, x: R.x[0], z: R.z[0], y: R.y[0], h: R.h[0], vh: R.h[0], latA: 0 });
        balls.push({ s: new THREE.Sphere(new THREE.Vector3(), KART_R), seen: false, at: -1e9 }); // its sphere where it was last posed (shadowBounds)
      }
    }
    let cx = 0, cy = 0, cz = 0; for (let i = 0; i < T.N; i++) { cx += T.x[i]; cy += T.y[i]; cz += T.z[i]; }
    const center = new THREE.Vector3(cx / T.N, cy / T.N, cz / T.N); // (instanced meshes have no useful bounds yet)
    scene.add(group);
    state.push({ cfg, T, R, group, karts, balls, parts, beams, pools: poolsK, spray, glow, lampPools: pools, V, center, active: true, lay, clock: 0 }); // WS-41: active (setSeason); VIS-267 step 3: balls, clock = the track's own time (s)
  }

  const m4 = new THREE.Matrix4(), w4 = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(0, 0, 0, 'YZX'), p = new THREE.Vector3(), one = new THREE.Vector3(1, 1, 1), sc = new THREE.Vector3();
  const wq = new THREE.Quaternion(), we = new THREE.Euler(0, 0, 0, 'YXZ'), wp = new THREE.Vector3();

  function stepTrack(S, dt) {
    const { R, karts, V } = S;
    if (!karts.length) return;
    const order = karts.map((k, i) => i).sort((a, b) => karts[a].s - karts[b].s);
    const minGap = V.minGap ?? 6, accel = V.accel ?? 2.6, brake = V.brake ?? 5, vTop = V.topSpeed ?? 11;
    for (let oi = 0; oi < order.length; oi++) {
      const k = karts[order[oi]], ahead = karts[order[(oi + 1) % order.length]];
      const gap = karts.length > 1 ? (((ahead.s - k.s) % R.len) + R.len) % R.len : Infinity;
      let target = R.v[R.at(k.s + Math.max(1, k.v * 0.3)).i] * k.skill; // a short look-ahead on the speed profile
      if (gap < minGap * 2) target = Math.min(target, ahead.v + (gap - minGap) * 0.9); // close up, never ram
      const dv = target - k.v;
      const a = dv > 0 ? Math.min(dv, accel * (1 - 0.45 * k.v / vTop) * dt) : Math.max(dv, -brake * dt);
      k.braking += ((dv < -0.15 ? 1 : 0) - k.braking) * Math.min(1, dt * 6);
      k.throttle += ((dv > 0.15 ? 1 : 0) - k.throttle) * Math.min(1, dt * 6);
      k.v = Math.max(0, k.v + a);
      k.s = (k.s + k.v * dt) % R.len;
      const c = R.at(k.s);
      const latA = k.v * k.v * c.k;
      // Ice: the rear steps out a little in bends and under power on the exit; smoothed so nothing snaps.
      const betaT = THREE.MathUtils.clamp(latA / (V.cornerAccel ?? 3), -1, 1) * (V.slide ?? 0.1) * (1 + 0.6 * k.throttle);
      k.beta += (betaT - k.beta) * Math.min(1, dt * 3);
      // Front wheels: geometric steer for the line's curvature, eased by a little counter-steer for the slide
      // (a mild slide on ice: the wheels still point into the bend; full opposite lock would read as a spin).
      const steerT = THREE.MathUtils.clamp(Math.atan(KART_WHEELBASE * c.k) - k.beta * 0.35, -0.5, 0.5);
      k.steer += (steerT - k.steer) * Math.min(1, dt * 6);
      k.lat += (k.lane - k.lat) * Math.min(1, dt * 0.5);
      k.spin += (k.v * dt) / 0.13;
      k.pitch += ((k.braking > 0.5 ? -0.025 : k.throttle > 0.5 ? 0.01 : 0) - k.pitch) * Math.min(1, dt * 5);
      const nx = -Math.sin(c.h), nz = Math.cos(c.h);
      k.x = c.x + nx * k.lat; k.z = c.z + nz * k.lat; k.y = c.y + 0.07; k.h = c.h + k.beta; k.vh = c.h;
      k.latA = latA;
      k.emit += (Math.abs(k.beta) > 0.04 ? 50 * Math.abs(k.beta) / 0.1 : 0) * dt + (k.braking > 0.5 ? 20 : 1.5) * dt;
    }
  }

  function poseKart(S, set, i, k) { // kart i's matrices in the parts of `set`, its beam and pool
    const wheels = set[set.length - 1];
    e.set(0, -k.h, k.pitch);
    q.setFromEuler(e);
    m4.compose(p.set(k.x, k.y, k.z), q, one);
    for (let j = 0; j < set.length - 1; j++) set[j].setMatrixAt(i, m4);
    KART_WHEELS.forEach((wh, j) => {
      we.set(0, wh.front ? -k.steer : 0, -k.spin); // steer about y, then roll about the axle (z)
      wq.setFromEuler(we);
      w4.compose(wp.set(wh.x, wh.r, wh.z), wq, sc.set(wh.r, wh.r, wh.w));
      wheels.setMatrixAt(i * 4 + j, w4.premultiply(m4));
    });
    if (S.beams) { e.set(0, -k.vh, 0); q.setFromEuler(e); m4.compose(p.set(k.x, k.y, k.z), q, one); S.beams.setMatrixAt(i, m4); }
    if (S.pools?.visible) { const L = S.pools.userData.local; S.lay(S.pools.geometry.attributes.position.array, i * L.length, L, k.x, k.z, k.vh, S.R.ti[S.R.at(k.s).i]); } // (after dark only: ~70 heights a kart)
    S.balls[i].s.center.set(k.x, k.y + 0.35, k.z); // where it is drawn now
  }
  function emit(spray, k) { // the spray its tyres threw since its last pose
    while (k.emit >= 1) {
      k.emit -= 1;
      const j = spray.next; spray.next = (j + 1) % spray.N;
      const side = Math.random() < 0.5 ? -1 : 1, cx = Math.cos(k.h), cz = Math.sin(k.h);
      const out = -Math.sign(k.latA || 1), sp = 0.6 + Math.random() * 1.6; // outside of the bend = -sign(curvature) x left normal (-cz, cx)
      spray.pos.set([k.x - cx * 0.62 - cz * side * 0.62, k.y + 0.06, k.z - cz * 0.62 + cx * side * 0.62], j * 3);
      spray.vel.set([-cx * k.v * 0.2 - cz * out * sp + (Math.random() - 0.5) * 0.6, 0.5 + Math.random() * 1.0, -cz * k.v * 0.2 + cx * out * sp + (Math.random() - 0.5) * 0.6], j * 3);
      spray.life[j] = 1; spray.fresh = true;
    }
  }

  // VIS-267 step 3: the karts' pose. Each kart asks lifePace (its sphere, grown to its beam's reach while the lights are
  // on); posed: its matrices, its spray, its sphere; else nothing is touched (no matrix, no needsUpdate: the rest and the
  // shadow keeper see no change). The first frame places every kart (a kart never posed would stand at the map's
  // origin); a new detail set takes the other set's matrices as they are (every kart stays where it was drawn).
  function writeInstances(S, camera, dt) {
    const { karts, parts, beams, pools, spray, balls } = S;
    if (!parts) return;
    S.clock += dt;
    const useHi = capture.on || !camera || camera.position.distanceTo(S.center) < (S.V.lodDistance ?? 160); // a capture (capture.js): the full karts (and their shadows) at any distance
    if (useHi !== parts.useHi) {
      const from = parts.useHi ? parts.hi : parts.lo, to = useHi ? parts.hi : parts.lo;
      to.forEach((m, j) => { m.instanceMatrix.array.set((j === to.length - 1 ? from[from.length - 1] : from[0]).instanceMatrix.array); m.instanceMatrix.needsUpdate = true; m.computeBoundingSphere(); }); // (body, plate and lamps share the body's matrix; the wheels the wheels')
      parts.useHi = useHi;
      for (const m of parts.hi) { m.visible = useHi; }
      for (const m of parts.lo) { m.visible = !useHi; }
    }
    const set = useHi ? parts.hi : parts.lo, r = beams?.visible ? BEAM_R : KART_R;
    let posed = !S.placed;
    if (pools && pools.visible !== S.poolsOn) { S.poolsOn = pools.visible; if (pools.visible) { karts.forEach((k, i) => poseKart(S, set, i, k)); posed = true; } } // (the pools lit at dusk: every kart's laid once, where it is drawn)
    if (!S.placed) { // the first frame: every kart where its race starts (its last drawn spot for lifePace: pose() keeps it in lifeAt)
      S.placed = true;
      karts.forEach((k, i) => { poseKart(S, set, i, k); k.lifeAt = [k.x, k.y + 0.35, k.z]; });
    }
    karts.forEach((k, i) => {
      const d = lifePace.pose(k, k.x, k.y + 0.35, k.z, r, KART_H);
      if (d > 0) {
        poseKart(S, set, i, k); balls[i].at = S.clock; posed = true;
        if (spray) { if (d > 0.25) k.emit = Math.min(k.emit, 3); emit(spray, k); } // (back in view, or a far tick: a puff, not every one thrown meanwhile)
      }
      balls[i].seen = S.clock - balls[i].at < 1; // (shadowBounds: posed in the last second, at least on the far tick)
    });
    if (!posed) return;
    for (const m of set) { m.instanceMatrix.needsUpdate = true; m.computeBoundingSphere(); }
    if (beams) { beams.instanceMatrix.needsUpdate = true; beams.computeBoundingSphere(); }
    if (pools?.visible) pools.geometry.attributes.position.needsUpdate = true;
  }

  const box = new THREE.Box3(), pv = new THREE.Vector3();
  function stepSpray(S, dt) {
    const sp = S.spray;
    if (!sp || (!sp.alive && !sp.fresh)) return; // VIS-267 step 3: nothing in the air: nothing stepped, nothing uploaded
    sp.fresh = false;
    let alive = 0;
    box.makeEmpty();
    for (let j = 0; j < sp.N; j++) {
      if (sp.life[j] <= 0) continue;
      sp.life[j] = Math.max(0, sp.life[j] - dt * 1.5);
      sp.vel[j * 3 + 1] -= 4.5 * dt;
      for (let a = 0; a < 3; a++) sp.pos[j * 3 + a] += sp.vel[j * 3 + a] * dt;
      if (sp.life[j] > 0) { alive++; box.expandByPoint(pv.fromArray(sp.pos, j * 3)); }
    }
    sp.alive = alive;
    if (alive) { box.getBoundingSphere(sp.ball); sp.ball.radius += 0.5; } // (a puff is a few cm to 0.3 m across)
    sp.posA.needsUpdate = true;
    sp.lifeA.needsUpdate = true;
  }

  return {
    count: state.length,
    tracks: state.map((s) => ({ id: s.cfg.id, place: s.cfg.place, length: s.T.len, line: s.R?.len })),
    setLights(k) {
      lightK = k;
      for (const S of state) {
        if (S.glow) { S.glow.material.opacity = Math.min(1, k * 1.1); S.glow.visible = k > 0.02; S.lampPools.material.opacity = Math.min(0.45, k * 0.6); S.lampPools.visible = k > 0.02; }
        if (S.beams) { S.beams.material.uniforms.k.value = k; S.pools.material.uniforms.k.value = k; S.beams.visible = S.pools.visible = k > 0.02; }
        if (S.parts) { S.parts.mats.ledFMat.emissiveIntensity = 1.2 + 6 * k; S.parts.mats.ledRMat.emissiveIntensity = 0.8 + 3 * k; }
      }
    },
    update(dt, t, camera, controls) {
      for (const S of state) {
        if (!S.active) { for (const k of S.karts) lifePace.away(k); continue; } // WS-41: out of season = still + hidden (VIS-267 step 3: its next look is an entry)
        if (!S.film) stepTrack(S, dt); // VIS-265: Film holds the karts (film)
        writeInstances(S, camera, dt); stepSpray(S, dt);
      }
    },
    /** VIS-265 Film (lazy/film.js only; inert unless called): the karts of track S stand where Film's clock puts them,
     *  `f` = [{ s, v, d }] a kart (racing-line metres, m/s, metres gone: the wheels roll by it; filmshot.js kartAt), no
     *  slide, no history; null: the race carries on from where they stand. */
    film(S, f) {
      S.film = f;
      f?.forEach((F, i) => { const k = S.karts[i], c = S.R.at(F.s); if (k) Object.assign(k, { s: F.s, v: F.v, lat: k.lane, beta: 0, pitch: 0, braking: 0, throttle: 0, emit: 0, latA: 0, spin: F.d / 0.13 + i * 1.7,
        steer: THREE.MathUtils.clamp(Math.atan(KART_WHEELBASE * c.k), -0.5, 0.5), x: c.x - Math.sin(c.h) * k.lane, z: c.z + Math.cos(c.h) * k.lane, y: c.y + 0.07, h: c.h, vh: c.h }); });
    },
    /** WS-85: the tracks with karts and a place, for the ride view's kart mount (lazy/rideview.js: "Watch a race" on the
     *  card, the race camera, the karts as a pick): { cfg, karts, R (racing line), active } each. */
    views: () => state.filter((S) => S.karts.length && S.cfg.place),
    heightAt,
    /** WS-41 live season switch: a track whose config declares `seasons` (an ice track) is drawn and driven only in
     *  those; out of season its group (surface, banks, lamps, karts) is hidden (a race camera on it ends: lazy/rideview.js). */
    setSeason(s) {
      for (const S of state) {
        S.inSeason = !Array.isArray(S.cfg.seasons) || S.cfg.seasons.includes(s);
        S.group.visible = S.active = S.inSeason && !S.off; // VIS-87: nor while its place is hidden (setOff)
      }
    },
    /** VIS-87: the tracks whose place `hidden(place)` says is hidden stop and go off the map (the editor's frame, live: lazy/places.js). */
    setOff(hidden) {
      for (const S of state) {
        S.off = !!S.cfg.place && !!hidden(S.cfg.place);
        S.group.visible = S.active = S.inSeason !== false && !S.off;
      }
    },
    /** VIS-87: the group of a track's surface, banks and lamps (lazy/places.js draws it faded while its place is hidden). */
    groupOf: (place) => state.find((S) => S.cfg.place === place && S.inSeason !== false)?.group || null,
    get lightK() { return lightK; },
    /** QA: kart states of a track. */
    karts: (id) => (state.find((s) => s.cfg.id === id)?.karts || []).map((k) => ({ x: k.x, z: k.z, v: k.v, s: k.s, beta: k.beta, steer: k.steer })),
  };
}

/** Racing line: the centreline relaxed towards minimum curvature inside +-lim (outside - apex - outside),
 *  resampled every 1 m, with heading, curvature and a speed profile (corner limit, then acceleration forwards
 *  and braking backwards, repeated around the loop). */
function racingLine(T, lim, V) {
  const N = T.N, px = Float32Array.from(T.x), pz = Float32Array.from(T.z), off = new Float32Array(N);
  const nX = (i) => -Math.sin(T.hd[i]), nZ = (i) => Math.cos(T.hd[i]);
  for (let it = 0; it < 600; it++) {
    for (let i = 0; i < N; i++) {
      const a = T.idx(i - 3), b = T.idx(i + 3);
      const mx = (px[a] + px[b]) / 2, mz = (pz[a] + pz[b]) / 2;
      const o = THREE.MathUtils.clamp(off[i] + ((mx - px[i]) * nX(i) + (mz - pz[i]) * nZ(i)) * 0.5, -lim, lim);
      off[i] = o; px[i] = T.x[i] + nX(i) * o; pz[i] = T.z[i] + nZ(i) * o;
    }
  }
  const cum = new Float64Array(N + 1); // resample the line by its own arc length
  for (let i = 0; i < N; i++) { const j = T.idx(i + 1); cum[i + 1] = cum[i] + Math.hypot(px[j] - px[i], pz[j] - pz[i]); }
  const len = T.closed ? cum[N] : cum[N - 1], M = Math.max(16, Math.round(len)), ds = len / M;
  const x = new Float32Array(M), z = new Float32Array(M), y = new Float32Array(M), h = new Float32Array(M), k = new Float32Array(M), v = new Float32Array(M), ti = new Int32Array(M);
  for (let m = 0, i = 0; m < M; m++) {
    const s = m * ds;
    while (i < N - 1 && cum[i + 1] < s) i++;
    const j = T.idx(i + 1), u = (s - cum[i]) / Math.max(1e-6, cum[i + 1] - cum[i]);
    x[m] = px[i] + (px[j] - px[i]) * u; z[m] = pz[i] + (pz[j] - pz[i]) * u; y[m] = T.y[i] + (T.y[j] - T.y[i]) * u; ti[m] = u < 0.5 ? i : j;
  }
  const id = (m) => ((m % M) + M) % M;
  for (let m = 0; m < M; m++) h[m] = Math.atan2(z[id(m + 1)] - z[id(m - 1)], x[id(m + 1)] - x[id(m - 1)]);
  const kr = new Float32Array(M);
  for (let m = 0; m < M; m++) { let d = h[id(m + 2)] - h[id(m - 2)]; d = Math.atan2(Math.sin(d), Math.cos(d)); kr[m] = d / (4 * ds); }
  for (let m = 0; m < M; m++) { let s = 0; for (let j = -3; j <= 3; j++) s += kr[id(m + j)]; k[m] = s / 7; }
  const vTop = V.topSpeed ?? 11, aLat = V.cornerAccel ?? 3, acc = V.accel ?? 2.6, brk = V.brake ?? 5;
  for (let m = 0; m < M; m++) v[m] = Math.min(vTop, Math.sqrt(aLat / Math.max(1e-4, Math.abs(k[m]))));
  for (let pass = 0; pass < 3; pass++) {
    for (let m = 1; m <= M; m++) { const a = id(m - 1), b = id(m); v[b] = Math.min(v[b], Math.sqrt(v[a] ** 2 + 2 * acc * (1 - 0.45 * v[a] / vTop) * ds)); }
    for (let m = M - 1; m >= 0; m--) { const a = id(m), b = id(m + 1); v[a] = Math.min(v[a], Math.sqrt(v[b] ** 2 + 2 * brk * ds)); }
  }
  const at = (s) => {
    const f = (((s % len) + len) % len) / ds, i0 = Math.floor(f) % M, i1 = id(i0 + 1), u = f - Math.floor(f);
    let dh = h[i1] - h[i0]; dh = Math.atan2(Math.sin(dh), Math.cos(dh));
    return { i: i0, x: x[i0] + (x[i1] - x[i0]) * u, z: z[i0] + (z[i1] - z[i0]) * u, y: y[i0] + (y[i1] - y[i0]) * u, h: h[i0] + dh * u, k: k[i0] + (k[i1] - k[i0]) * u };
  };
  return { len, ds, x, z, y, h, k, v, at, off, ti }; // ti: the centreline sample under each point (where the pools are laid from)
}
